import React, { useState, useRef, useCallback, useEffect } from "react";
import { TextField, Select, SortMenu, Button } from "@ann/web-components";
import {
  FiltersContainer,
  FiltersLeftContent,
  FiltersRightContent,
} from "./Filters.styled";

const Filters = ({
  loading = false,
  filters,
  filterHandlers,
  filterUI,
  sorting,
}) => {
  const [searchValue, setSearchValue] = useState(filters?.searchValue || "");
  const [filtersKey, setFiltersKey] = useState(filterUI?.filtersKey || 0);
  const searchTimeoutRef = useRef(null);

  // Desestructurar valores de los objetos agrupados
  const {
    statusOptions,
    expirationOptions,
    sourceOptions,
    selectedStatus,
    selectedExpiration,
    selectedSource,
    hasActiveFilters,
  } = filters;

  const {
    handleSearchChange,
    handleStatusChange,
    handleExpirationChange,
    handleSourceChange,
    handleClearAllFiltersWithKeys,
  } = filterHandlers;

  const {
    selectedSortOption,
    orderDirection,
    handleSortChange,
    handleOrderChange,
  } = sorting;

  const handleSearchChangeCallback = useCallback((value) => {
    setSearchValue(value);
    
    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    
    // Set new timeout for 500ms debounce
    searchTimeoutRef.current = setTimeout(() => {
      if (handleSearchChange) {
        handleSearchChange(value);
      }
    }, 500);
  }, [handleSearchChange]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, []);

  // Sync local state with filters when it changes
  useEffect(() => {
    setSearchValue(filters?.searchValue || "");
  }, [filters?.searchValue]);

  // Sincronizar filtersKey cuando cambia desde el hook
  useEffect(() => {
    if (filterUI?.filtersKey !== undefined) {
      setFiltersKey(filterUI.filtersKey);
    }
  }, [filterUI?.filtersKey]);

  return (
    <FiltersContainer>
      <FiltersLeftContent>
        <TextField
          iconLeft="search"
          onChange={handleSearchChangeCallback}
          size="small"
          placeholder="Search"
          value={searchValue}
          disabled={loading}
        />
        <Select
          key={`status-filter-${filtersKey}`}
          label="Status"
          onChange={handleStatusChange}
          placeholder="Status"
          size="small"
          value={statusOptions.find((option) => option.value === selectedStatus) || undefined}
          width="auto"
          minWidth="60px"
          disabled={loading}
        >
          <Select.Menu width="140px">
            {statusOptions.map((option) => (
              <Select.Item
                key={option.value}
                value={option.value}
                label={option.label}
              >
                {option.label}
              </Select.Item>
            ))}
          </Select.Menu>
        </Select>
        <Select
          key={`expiration-filter-${filtersKey}`}
          label="Expiration"
          onChange={handleExpirationChange}
          placeholder="Expiration"
          size="small"
          value={expirationOptions.find((option) => option.value === selectedExpiration) || undefined}
          width="auto"
          minWidth="60px"
          disabled={loading}
        >
          <Select.Menu width="140px">
            {expirationOptions.map((option) => (
              <Select.Item
                key={option.value}
                value={option.value}
                label={option.label}
              >
                {option.label}
              </Select.Item>
            ))}
          </Select.Menu>
        </Select>
        <Select
          key={`source-filter-${filtersKey}`}
          label="Source"
          onChange={handleSourceChange}
          placeholder="Source"
          size="small"
          value={sourceOptions.find((option) => option.value === selectedSource) || undefined}
          width="auto"
          minWidth="60px"
          disabled={loading}
        >
          <Select.Menu width="140px">
            {sourceOptions.map((option) => (
              <Select.Item
                key={option.value}
                value={option.value}
                label={option.label}
              >
                {option.label}
              </Select.Item>
            ))}
          </Select.Menu>
        </Select>
        {hasActiveFilters && (
          <Button
            icon=""
            iconLeft=""
            iconRight=""
            label="Clear Filters"
            size="medium"
            type="secondary"
            onClick={handleClearAllFiltersWithKeys}
            disabled={loading}
          />
        )}
      </FiltersLeftContent>
      <FiltersRightContent>
        <SortMenu
          options={[
            { value: "name", label: "Name" },
            { value: "status", label: "Status" },
            { value: "updated", label: "Last Updated" },
          ]}
          selectedOption={selectedSortOption}
          orderDirection={orderDirection}
          onChange={handleSortChange}
          onOrderChange={handleOrderChange}
        />
      </FiltersRightContent>
    </FiltersContainer>
  );
};

export default Filters;
